import { supabase } from "./dataService";
import { buildReceivePurchaseLines } from "../utils/purchaseReceiving";

function purchaseServiceError(message, details) {
  const error = new Error(message);
  error.response = { data: { error: message, details } };
  return error;
}

const safeDetails = (details) => {
  if (details && typeof details === "object") return details;
  if (typeof details !== "string" || !details.trim()) return undefined;
  try {
    const parsed = JSON.parse(details);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const knownReceiveValidationMessages = {
  "Choose an active supplier":
    "The selected supplier is no longer active. Choose another supplier.",
  "A purchase product no longer exists":
    "A product in this purchase no longer exists. Remove it and add the current product.",
  "A selected packaging unit is unavailable":
    "A packaging unit in this purchase is no longer available. Remove that line and add the current unit.",
  "Select a packaging unit for this product":
    "A product in this purchase now requires a packaging unit. Remove that line and add the correct unit.",
};

const mapError = (error, operation) => {
  if (error?.code === "42501") {
    return purchaseServiceError("Administrator access is required.");
  }
  if (operation === "void" && error?.code === "23514") {
    return purchaseServiceError(
      "This purchase cannot be voided because some of its stock is no longer available.",
      safeDetails(error.details),
    );
  }
  if (operation === "supplier" && error?.code === "23505") {
    return purchaseServiceError("A supplier with this company name already exists.");
  }
  if (operation === "receive" && error?.code === "23505") {
    return purchaseServiceError(
      "A scanned barcode now belongs to another product. Scan it again to choose the current item.",
    );
  }
  if (
    operation === "receive" &&
    ["22023", "P0002"].includes(error?.code) &&
    knownReceiveValidationMessages[error?.message]
  ) {
    return purchaseServiceError(knownReceiveValidationMessages[error.message]);
  }
  if (["22023", "P0002"].includes(error?.code)) {
    const message =
      operation === "supplier"
        ? "Check the supplier details and try again."
        : operation === "void"
          ? "This purchase cannot be voided in its current state."
          : "A supplier, product, packaging unit, quantity, or price is no longer valid. Review the purchase and try again.";
    return purchaseServiceError(message, safeDetails(error.details));
  }
  const fallback = {
    supplier: "Supplier information could not be saved.",
    receive: "The purchase could not be received. No stock was changed.",
    void: "The purchase could not be voided. No stock was changed.",
    list: "Purchase information could not be loaded.",
  }[operation];
  return purchaseServiceError(fallback || "Purchase operation failed.");
};

const normalizeSupplier = (supplier) => ({
  id: supplier.id,
  companyName: supplier.companyName ?? supplier.company_name,
  phone: supplier.phone ?? null,
  address: supplier.address ?? null,
  active: supplier.active,
  createdAt: supplier.createdAt ?? supplier.created_at,
  updatedAt: supplier.updatedAt ?? supplier.updated_at,
});

const normalizePurchaseItem = (item) => ({
  id: item.id,
  itemId: item.item_id,
  productUnitId: item.product_unit_id,
  itemName: item.item_name,
  unitName: item.unit_name,
  barcode: item.barcode,
  packageQuantity: item.package_quantity,
  conversionFactor: item.conversion_factor,
  receivedBaseQuantity: item.received_base_quantity,
  unitBuyPrice: item.unit_buy_price,
  lineTotal: item.line_total,
  selectedUnitSellPrice: item.selected_unit_sell_price,
  appliedBaseBuyPrice: item.applied_base_buy_price,
  appliedBaseSellPrice: item.applied_base_sell_price,
  createdItem: item.created_item,
});

const normalizePurchase = (purchase) => ({
  id: purchase.id,
  purchaseNumber: purchase.purchase_number,
  supplierId: purchase.supplier_id,
  supplier: purchase.supplier ? normalizeSupplier(purchase.supplier) : null,
  purchasedAt: purchase.purchased_at,
  status: purchase.status,
  totalAmount: purchase.total_amount,
  createdAt: purchase.created_at,
  voidedAt: purchase.voided_at,
  voidReason: purchase.void_reason,
  items: (purchase.items || []).map(normalizePurchaseItem),
});

export async function listSuppliers({ includeInactive = false } = {}) {
  let query = supabase
    .from("suppliers")
    .select("id, company_name, phone, address, active, created_at, updated_at");
  if (!includeInactive) query = query.eq("active", true);
  const { data, error } = await query.order("company_name", { ascending: true });
  if (error) throw mapError(error, "list");
  return (data || []).map(normalizeSupplier);
}

export async function createSupplier({ companyName, phone, address }) {
  const { data, error } = await supabase.rpc("create_supplier", {
    p_company_name: String(companyName || "").trim(),
    p_phone: String(phone || "").trim() || null,
    p_address: String(address || "").trim() || null,
  });
  if (error) throw mapError(error, "supplier");
  return normalizeSupplier(data);
}

export async function setSupplierActive(supplierId, isActive) {
  const { data, error } = await supabase.rpc("set_supplier_active", {
    p_supplier_id: Number(supplierId),
    p_active: Boolean(isActive),
  });
  if (error) throw mapError(error, "supplier");
  return normalizeSupplier(data);
}

export async function listPurchases({
  dateFrom,
  dateTo,
  supplierId,
  status,
} = {}) {
  let query = supabase.from("purchases").select(`
    id, purchase_number, supplier_id, purchased_at, status, total_amount,
    created_at, voided_at, void_reason,
    supplier:suppliers(id, company_name, phone, address, active),
    items:purchase_items(
      id, item_id, product_unit_id, item_name, unit_name, barcode,
      package_quantity, conversion_factor, received_base_quantity,
      unit_buy_price, line_total, selected_unit_sell_price,
      applied_base_buy_price, applied_base_sell_price, created_item
    )
  `);
  if (dateFrom) query = query.gte("purchased_at", dateFrom);
  if (dateTo) query = query.lte("purchased_at", dateTo);
  if (supplierId) query = query.eq("supplier_id", Number(supplierId));
  if (status) query = query.eq("status", status);
  const { data, error } = await query.order("purchased_at", { ascending: false });
  if (error) throw mapError(error, "list");
  return (data || []).map(normalizePurchase);
}

export async function receivePurchase({
  supplierId,
  purchasedAt,
  idempotencyKey,
  lines,
}) {
  const { data, error } = await supabase.rpc("receive_purchase", {
    p_supplier_id: Number(supplierId),
    p_idempotency_key: idempotencyKey,
    p_purchased_at: purchasedAt || null,
    p_lines: buildReceivePurchaseLines(lines || []),
  });
  if (error) throw mapError(error, "receive");
  return data;
}

export async function voidPurchase({ purchaseId, reason }) {
  const { data, error } = await supabase.rpc("void_purchase", {
    p_purchase_id: Number(purchaseId),
    p_reason: String(reason || "").trim(),
  });
  if (error) throw mapError(error, "void");
  return data;
}
